import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Keyboard,
  StyleSheet,
  TextInput,
  Platform,
  KeyboardAvoidingView,
} from "react-native";
import i18n from "@microdoing/shared/i18n";
import { MainText } from "app/components/common/MainText";
import BottomSheet, { BottomSheetBackdrop } from "@gorhom/bottom-sheet";
import MyBottomSheet from "./MyBottomSheet/MyBottomSheet";
import { Box } from "app/components/common/Box/MainBox/MainBox";
import { isWeb, isWebDesktop } from "@microdoing/shared/utils/deviceDetectors";
import Handle from "./Handle";
import { useDispatch, useSelector } from "react-redux";
import Animated from "react-native-reanimated";
import {
  getCampaignId,
  getStepSelected,
  getStepSelectedId,
  getUserCampaignHasPeerToPeer,
  getUserCampaignStepCommentRequesting,
} from "@microdoing/shared/services/userCampaign/selectors";
import {
  getStepCommentInfoByStepId,
  getStepCommentsByStepId,
  sendResponseToStepComment,
  updateStepCommentRequest,
} from "@microdoing/shared/services/userCampaign/actions";
import theme from "app/Theme/Theme";
import { FlatList } from "react-native-gesture-handler";
import normalize from "app/utils/normalize";
import { widthPercentageToDP } from "react-native-responsive-screen";
import CommentSkeleton from "./CommentSkeleton";
import useTagUser from "app/hooks/useTagUser";
import SocialSheetInfos from "./SocialSheetInfos";
import Commentary from "app/components/common/Commentary/Commentary";
import { InputChat } from "app/scenes/MessageScreen/InputChat";
import { useAnimatedStyle } from "react-native-reanimated";
import { useAnimatedKeyboard } from "react-native-reanimated";
import { withTiming } from "react-native-reanimated";

const ReanimatedBox = Animated.createAnimatedComponent(Box);

const styles = StyleSheet.create({
  infos: {
    width: widthPercentageToDP("100%"),
    position: "relative",
    shadowColor: "#F4F4F4",
    shadowOpacity: 1,
    shadowOffset: {
      height: -5,
      width: 0,
    },
    shadowRadius: 26,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexDirection: "column",
    paddingHorizontal: theme.spacing.xl,
    overflow: "scroll",
    zIndex: normalize(3),
    elevation: normalize(3),
  },
  star: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "row",
  },
});

interface User {
  firstName: string;
  picture: string;
}

interface Props {
  rating?: number;
  isOpen?: boolean;
  isSmall?: boolean;
  numberOfComments?: number;
  users: User[];
  totalDone?: number;
  button?: React.ReactNode;
  contentTitle?: string;
  closedSnapPoint?: number;
  hasShadow?: boolean;
  style?: any;
}

const SocialSheet = ({
  rating = 5,
  isOpen = false,
  numberOfComments = 13,
  users = [
    {
      firstName: "Clem",
      picture:
        "https://img.freepik.com/vecteurs-libre/homme-affaires-caractere-avatar-isole_24877-60111.jpg?w=2000",
    },
    {
      firstName: "Armand",
      picture: "",
    },
  ],
  isSmall = false,
  button,
  totalDone = 0,
  contentTitle = "",
  closedSnapPoint = normalize(140),
  hasShadow = true,
  style = {},
}: Props) => {
  const bottomSheetRef = useRef<BottomSheet>(null);
  let isMounted = true; // Add Boolean

  const renderBackdrop = useCallback(
    props => (
      <BottomSheetBackdrop {...props} opacity={0} pressBehavior="collapse" />
    ),
    [],
  );

  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false);

  const [openAnswer, setOpenAnswer] = useState(false);
  const [stepCommentId, setStepCommentId] = useState("");
  const [receiver, setReceiver] = useState({ _id: "" });
  const [isModifier, setIsModifier] = useState(false);
  const textInputRef = useRef(null);
  const flatListRef = useRef<any>(null);

  const stepSelected = useSelector(getStepSelected);
  const stepSelectedId = useSelector(getStepSelectedId);
  const stepCommentRequesting = useSelector(
    getUserCampaignStepCommentRequesting,
  );
  const hasPeerToPeer = useSelector(getUserCampaignHasPeerToPeer);
  const campaignId = useSelector(getCampaignId);

  const dispatch = useDispatch();

  const [
    textFormatWithColor,
    textFormatInString,
    addTagUser,
    formatTextColor,
    formatTaggedUserBackToFront,
    formatTextFromToBack,
    resetAllTag,
  ] = useTagUser({ isNotFromInput: false });

  const keyboard = useAnimatedKeyboard();

  const animatedInputStyle = useAnimatedStyle(() => {
    return {
      transform: [
        {
          translateY: keyboard.height.value
            ? -keyboard.height.value
            : withTiming(0),
        },
      ],
    };
  }, []);

  // ^^ start section handling bottom sheet
  const closeSheet = () => {
    setIsBottomSheetOpen(false);
    resetAllTag();
    setOpenAnswer(false);
    Keyboard.dismiss();
    bottomSheetRef?.current?.snapToIndex(0);
  };

  const openSheet = useCallback(() => {
    dispatch(getStepCommentsByStepId({ stepId: stepSelectedId }));
    dispatch(getStepCommentInfoByStepId({ stepId: stepSelectedId }));
    setIsBottomSheetOpen(true);
    bottomSheetRef?.current?.expand();
  }, [stepSelectedId]);

  useEffect(() => {
    if (isWeb) {
      // Solve scrolling issue of bottom sheet on web
      const bottomSheetDom = document?.querySelector(
        '[aria-label="Bottom Sheet"] + *',
      );
      if (bottomSheetDom) {
        bottomSheetDom.style.overflow = "scroll";
      }
    }

    isMounted = true;
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (isOpen && !isBottomSheetOpen && hasPeerToPeer) {
      setTimeout(() => {
        if (isMounted) {
          openSheet();
        }
      }, 300);
    }
  }, [isOpen, stepSelectedId, openSheet, isMounted]);

  // variables
  const snapPoints = useMemo(
    () => [isSmall ? normalize(100) : closedSnapPoint, "92%"],
    [],
  );

  const handlePressComment = useCallback(() => {
    if (isBottomSheetOpen) {
      closeSheet();
    } else if (hasPeerToPeer) {
      openSheet();
    }
  }, [isBottomSheetOpen, closeSheet, openSheet]);

  // callbacks
  const handleSheetChanges = useCallback(
    (index: number) => {
      if (index === 0 && isBottomSheetOpen) {
        closeSheet();
      } else if (index === 1 && !isBottomSheetOpen && hasPeerToPeer) {
        openSheet();
      }
    },
    [isBottomSheetOpen, openSheet, closeSheet],
  );

  const scrollToBottom = useCallback(() => {
    flatListRef?.current?.scrollToEnd({ animating: true });
  }, []);
  // ^^end of section handling bottom sheet

  useEffect(() => {
    dispatch(getStepCommentInfoByStepId({ stepId: stepSelectedId }));
  }, [stepSelectedId]);

  // ^^ start of section for response
  const handleResponse = ({
    stepCommentId,
    isModifier = false,
    comment,
    receiver,
  }) => () => {
    addTagUser(
      {
        userId: receiver._id,
        name: receiver.firstName,
        startposition: 0,
        endPosition: receiver.firstName.length,
      },
      { isFirstWord: true },
    );
    setOpenAnswer(true);
    textInputRef?.current?.focus();
    setStepCommentId(stepCommentId);
    setIsModifier(isModifier);
    setReceiver(receiver);
  };

  const sendResponse = () => {
    const textToSend = formatTextFromToBack();
    if (!isModifier) {
      dispatch(
        sendResponseToStepComment({
          stepCommentId,
          comment: textToSend,
          stepId: stepSelectedId,
          receiverId: receiver?._id,
          campaignId,
        }),
      );
    } else {
      dispatch(
        updateStepCommentRequest({ stepCommentId, comment: textToSend }),
      );
    }

    setOpenAnswer(false);
    resetAllTag();
  };

  const changeText = newString => {
    formatTextColor(newString);
  };
  // ^^ end of section for response

  const onScroll = () => {
    Keyboard.dismiss();
  };

  const renderItem = useCallback(
    ({ item, index }) => (
      <Commentary
        key={index}
        commentary={item}
        marginTop={index === 0 ? "null" : "sm"}
        scrollToBottom={scrollToBottom}
        isLastComment={index === stepSelected?.comments?.length - 1}
        handleResponse={handleResponse}
      />
    ),
    [stepSelected],
  );

  const handleKeyExtractor = useCallback((item, index) => {
    return item._id;
  }, []);

  return (
    <MyBottomSheet
      style={style}
      isBottomSheetOpen={isBottomSheetOpen}
      enableOverDrag={true}
      bottomSheetRef={bottomSheetRef}
      backdropComponent={renderBackdrop}
      index={0}
      snapPoints={snapPoints}
      onChange={handleSheetChanges}
      containerStyle={[
        styles.infos,
        !hasShadow && { shadowColor: "transparent" },
      ]}
      handleComponent={Handle}
      enableHandlePanningGesture={hasPeerToPeer && numberOfComments > 0}
      enableContentPanningGesture={hasPeerToPeer && numberOfComments > 0}
    >
      <Box
        position={isWeb ? "fixed" : "relative"}
        backgroundColor="neutral.white"
        paddingHorizontal={isBottomSheetOpen ? "sm" : "l"}
        width="100%"
        style={
          isWeb && {
            marginTop: -theme.spacing.sl,
            paddingVertical: theme.spacing.s,
          }
        }
        marginBottom="xxs"
        zIndex={3}
      >
        <SocialSheetInfos
          contentTitle={contentTitle}
          rating={rating}
          numberOfComments={numberOfComments}
          users={users}
          totalDone={totalDone}
          onPressSocialSheet={handlePressComment}
          sheetOpen={isBottomSheetOpen}
        />
      </Box>
      {isBottomSheetOpen && (
        <Box
          marginTop={isWebDesktop ? "m" : "null"}
          width="100%"
          paddingVertical="xs"
          flexGrow={1}
          alignItems="center"
          justifyContent="center"
          paddingHorizontal={isWeb ? "null" : "sm"}
        >
          {stepSelected?.comments?.length < 1 && !stepCommentRequesting ? (
            <Box width="50%">
              <MainText
                variant="l4-medium"
                color="neutral.400"
                textAlign="center"
              >
                {i18n.t("commentary-no-comments")}
              </MainText>
            </Box>
          ) : (
            <Box flex={1} width="100%">
              <CommentSkeleton isLoading={stepCommentRequesting}>
                <FlatList
                  initialNumToRender={5}
                  ref={flatListRef}
                  data={stepSelected.comments}
                  keyExtractor={handleKeyExtractor}
                  showsVerticalScrollIndicator={false}
                  extraData={[
                    stepSelected.comments,
                    stepCommentId,
                    isBottomSheetOpen,
                  ]}
                  style={{
                    flex: 1,
                    display: "flex",
                    width: "100%",
                    paddingBottom: 50,
                  }}
                  onScrollBeginDrag={onScroll}
                  contentContainerStyle={{
                    paddingBottom: 70,
                    paddingTop: 10,
                    width: "100%",
                  }}
                  renderItem={renderItem}
                />
              </CommentSkeleton>
            </Box>
          )}
        </Box>
      )}
      {openAnswer && (
        <ReanimatedBox
          position={isWeb ? "fixed" : "absolute"}
          left={0}
          bottom={0}
          shadowColor="shadow"
          shadowOpacity={1}
          shadowOffset={{ width: 0, height: 0 }}
          shadowRadius={10}
          elevation={10}
          zIndex={10}
          width={widthPercentageToDP("100%")}
          flexDirection="row"
          backgroundColor="neutral.white"
          alignItems="center"
          height={60}
          style={animatedInputStyle}
        >
          <TextInput
            ref={textInputRef}
            style={{
              padding: theme.spacing.xs,
              backgroundColor: theme.colors["neutral.white"],
              color: theme.colors["neutral.900"],
              alignItems: "center",
              justifyContent: "center",
              flex: 1,
              outlineWidth: 0,
            }}
            multiline={true}
            numberOfLines={3}
            placeholderTextColor={theme.colors["neutral.500"]}
            autoFocus={openAnswer}
            onChangeText={changeText}
            placeholder={i18n.t("write-your-comment")}
          >
            {isWeb ? textFormatInString : textFormatWithColor}
          </TextInput>
          {!!textFormatInString && (
            <Box onTouchEnd={sendResponse}>
              <MainText variant="l4-semibold" margin="s" color="brand.500">
                {i18n.t("send")}
              </MainText>
            </Box>
          )}
        </ReanimatedBox>
      )}
      {!isBottomSheetOpen && !stepCommentRequesting && !!button && (
        <Box width="100%" paddingHorizontal="l">
          {button}
        </Box>
      )}
    </MyBottomSheet>
  );
};

export default memo(SocialSheet);
